import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAuthToken } from './authToken';

const API = '/api';
const auth = () => ({ Authorization: 'Bearer ' + getAuthToken() });
const NL = String.fromCharCode(10);

interface SelItem {
  pid: string;
  name: string;
  reference: string | null;
  ean: string | null;
  image_url: string | null;
  qty: number;
  unit: number;
  base: number | null;
  combination_id: string | null;
}

type Props = { onClose: () => void; onCreated: () => void };

const COUNTRIES: Record<string, string> = {
  PL: 'Polska', DE: 'Niemcy', CZ: 'Czechy', SK: 'Słowacja', UA: 'Ukraina',
  LT: 'Litwa', LV: 'Łotwa', EE: 'Estonia', GB: 'Wielka Brytania', AT: 'Austria',
  BE: 'Belgia', NL: 'Holandia', FR: 'Francja', IT: 'Włochy', ES: 'Hiszpania',
};

const PAYMENTS: Array<[string, string]> = [
  ['BANK_TRANSFER', 'Przelew bankowy'],
  ['CARD', 'Karta płatnicza'],
  ['CASH', 'Gotówka'],
  ['CASH_ON_DELIVERY', 'Za pobraniem (COD)'],
];
const PL_MAP: Record<string, string> = { 'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z' };
const normPl = (v: string): string => String(v || '').toLowerCase().replace(/[ąćęłńóśźż]/g, (ch) => PL_MAP[ch] || ch);
const PICKUP_CARRIER_RE = /(paczkomat|odbior|pickup|pick ?up|access ?point|parcel ?shop|packstation|service ?point|parcel locker|orlen paczka|one ?box|punkt)/;
const COD_CARRIER_RE = /(pobran|cash on delivery|\bcod\b)/;

const r2 = (x: number) => Math.round(x * 100) / 100;
const num = (v: string) => parseFloat(String(v || '').replace(',', '.')) || 0;

const parseWorkingAddress = (wa: string): { street: string; city: string; zip: string } => {
  const lines = String(wa || '').split(NL).map((x: string) => x.trim()).filter((x: string) => x.length > 0);
  for (let i = lines.length - 1; i >= 0; i--) {
    const ln = lines[i];
    if (ln.length >= 8 && ln.charAt(2) === '-' && /^[0-9]{2}-[0-9]{3}$/.test(ln.slice(0, 8))) {
      return { street: lines.slice(0, i).join(', '), city: ln.slice(9).trim(), zip: ln.slice(0, 8) };
    }
  }
  if (lines.length > 0) return { street: lines.slice(0, -1).join(', '), city: lines[lines.length - 1], zip: '' };
  return { street: '', city: '', zip: '' };
};

const s: any = {
  label: { fontSize: 12, color: '#6b7280', marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' as any, fontFamily: 'inherit', outline: 'none' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 },
  btn: { padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  btnPrimary: { padding: '8px 18px', borderRadius: 6, border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
};

export default function ManualOrderForm({ onClose, onCreated }: Props) {
  const [pq, setPq] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [ppage, setPpage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [ploading, setPloading] = useState(false);
  const [perror, setPerror] = useState('');
  const [notConfigured, setNotConfigured] = useState(false);
  const [pwarn, setPwarn] = useState<string[]>([]);
  const [sel, setSel] = useState<SelItem[]>([]);
  const [mult, setMult] = useState('');
  const [itemMult, setItemMult] = useState<Record<string, string>>({});
  const [carriers, setCarriers] = useState<any[]>([]);
  const [carrierId, setCarrierId] = useState('');
  const [deliveryCost, setDeliveryCost] = useState('');
  const [addr, setAddr] = useState({ firstName: '', lastName: '', companyName: '', email: '', phone: '', street: '', city: '', zip: '', country: 'PL' });
  const [invRequired, setInvRequired] = useState(false);
  const [invSame, setInvSame] = useState(true);
  const [nip, setNip] = useState('');
  const [nipLoading, setNipLoading] = useState(false);
  const [nipInfo, setNipInfo] = useState<any>(null);
  const [inv, setInv] = useState({ company: '', street: '', city: '', zip: '' });
  const [paymentType, setPaymentType] = useState('BANK_TRANSFER');
  const [pickupPoint, setPickupPoint] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<any>(null);
  const reqSeq = useRef(0);
  const [varOpen, setVarOpen] = useState<Record<string, boolean>>({});
  const [deliveryAuto, setDeliveryAuto] = useState(true);
  const [quote, setQuote] = useState<any>(null);
  const [payAuto, setPayAuto] = useState(false);

  const searchProducts = (q: string, page: number, append: boolean) => {
    const my = ++reqSeq.current;
    setPloading(true);
    setPerror('');
    axios.get(API + '/settings/prestashop/products', { params: { q: q, page: page, per_page: 12 }, headers: auth() })
      .then((r: any) => {
        if (my !== reqSeq.current) return;
        const list = (r.data && r.data.products) || [];
        setProducts((prev: any[]) => (append ? prev.concat(list) : list));
        setHasMore(!!(r.data && r.data.has_more));
        setPpage(page);
        setNotConfigured(false);
        setPwarn(((r.data && r.data.warnings) || []) as string[]);
      })
      .catch((e: any) => {
        if (my !== reqSeq.current) return;
        const d = (e.response && e.response.data && e.response.data.detail) || 'Nie udało się pobrać produktów ze sklepu.';
        setPerror(d);
        if (String(d).indexOf('Ustawieni') >= 0) setNotConfigured(true);
      })
      .finally(() => { if (my === reqSeq.current) setPloading(false); });
  };

  const showLess = () => {
    reqSeq.current++;
    setPloading(false);
    setProducts((prev: any[]) => prev.slice(0, 12));
    setPpage(1);
    setHasMore(true);
  };

  useEffect(() => {
    const t = window.setTimeout(() => searchProducts(pq, 1, false), 350);
    return () => window.clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pq]);

  useEffect(() => {
    axios.get(API + '/settings/prestashop/carriers', { headers: auth() })
      .then((r: any) => setCarriers((r.data && r.data.carriers) || []))
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAddrField = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setAddr((prev) => { const n: any = { ...prev }; n[k] = v; return n; });
  };

  const setItemMultValue = (pid: string, v: string) => {
    setItemMult((prev) => { const n: any = { ...prev }; n[pid] = v; return n; });
  };

  const addProduct = (p: any, c?: any) => {
    const pid = String(p.id) + (c ? ':' + String(c.id) : '');
    setSel((prev: SelItem[]) => {
      const ex = prev.find((x) => x.pid === pid);
      if (ex) return prev.map((x) => (x.pid === pid ? { ...x, qty: x.qty + 1 } : x));
      return prev.concat([{
        pid: pid,
        name: String(p.name || ('Produkt ' + String(p.id))) + (c ? ' — ' + String(c.label || c.reference || ('wariant ' + String(c.id))) : ''),
        reference: c ? (c.reference || null) : (p.reference || null),
        ean: c ? (c.ean13 || null) : (p.ean13 || null),
        image_url: (p.image_url || null),
        qty: 1,
        unit: Number(c ? c.price_brutto : p.price_brutto) || 0,
        base: ((c ? c.price_brutto_base : p.price_brutto_base) != null ? Number(c ? c.price_brutto_base : p.price_brutto_base) : null),
        combination_id: c ? String(c.id) : null,
      }]);
    });
  };

  const setQty = (pid: string, v: string) => {
    const q = Math.max(1, parseInt(v, 10) || 1);
    setSel((prev) => prev.map((x) => (x.pid === pid ? { ...x, qty: q } : x)));
  };

  const setUnit = (pid: string, v: string) => {
    setSel((prev) => prev.map((x) => (x.pid === pid ? { ...x, unit: num(v) } : x)));
  };

  const applyMultAll = () => {
    const m = num(mult);
    if (m <= 0) { setError('Mnożnik musi być większy od zera, np. 0,7 (rabat 30%).'); return; }
    setError('');
    setSel((prev) => prev.map((x) => ({ ...x, unit: r2(x.unit * m) })));
  };

  const applyMultItem = (pid: string) => {
    const m = num(itemMult[pid] || '');
    if (m <= 0) { setError('Mnożnik musi być większy od zera, np. 0,7 (rabat 30%).'); return; }
    setError('');
    setSel((prev) => prev.map((x) => (x.pid === pid ? { ...x, unit: r2(x.unit * m) } : x)));
  };

  const fetchNip = () => {
    const digits = nip.replace(/[^0-9]/g, '');
    if (digits.length !== 10) { setError('NIP musi mieć dokładnie 10 cyfr.'); return; }
    setNipLoading(true); setError(''); setNipInfo(null);
    axios.get(API + '/settings/prestashop/nip/' + digits, { headers: auth() })
      .then((r: any) => {
        setNipInfo(r.data);
        if (r.data && r.data.found) {
          setInv((prev) => {
            const n: any = { ...prev };
            n.company = r.data.name || prev.company;
            const wa = parseWorkingAddress(r.data.working_address || '');
            n.street = wa.street || prev.street;
            n.city = wa.city || prev.city;
            n.zip = wa.zip || prev.zip;
            return n;
          });
        }
      })
      .catch((e: any) => setError((e.response && e.response.data && e.response.data.detail) || 'Błąd pobierania danych z Białej Listy.'))
      .finally(() => setNipLoading(false));
  };

  const itemsTotal = r2(sel.reduce((a: number, it) => a + it.unit * it.qty, 0));
  const dcost = num(deliveryCost);
  const total = r2(itemsTotal + dcost);
  const carrierName = (() => {
    const c = carriers.find((x: any) => String(x.id) === carrierId);
    return c && c.name ? String(c.name) : null;
  })();
  const carrierNorm = normPl(carrierName || '');
  const isPickupCarrier = PICKUP_CARRIER_RE.test(carrierNorm);
  const carrierObj: any = carriers.find((x: any) => String(x.id) === carrierId) || null;
  const isCodCarrier = !!(carrierObj && carrierObj.is_cod) || COD_CARRIER_RE.test(carrierNorm);

  useEffect(() => {
    if (!carrierId) { setQuote(null); return; }
    let alive = true;
    const t = window.setTimeout(() => {
      axios.get(API + '/settings/prestashop/shipping-quote', { params: { carrier_id: carrierId, total: itemsTotal }, headers: auth() })
        .then((r: any) => {
          if (!alive) return;
          setQuote(r.data);
          if (deliveryAuto && r.data && r.data.cost != null) setDeliveryCost(Number(r.data.cost).toFixed(2));
        })
        .catch(() => { if (alive) setQuote(null); });
    }, 300);
    return () => { alive = false; window.clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierId, itemsTotal, deliveryAuto]);
  const deliveryMode: string = isPickupCarrier ? 'pickup' : 'address';

  const onCarrierChange = (v: string) => {
    setCarrierId(v);
    const c = carriers.find((x: any) => String(x.id) === v);
    const nn = normPl(c && c.name ? String(c.name) : '');
    const cod = !!(c && c.is_cod) || COD_CARRIER_RE.test(nn);
    if (cod) { setPaymentType('CASH_ON_DELIVERY'); setPayAuto(true); }
    else if (payAuto) { setPaymentType('BANK_TRANSFER'); setPayAuto(false); }
    setDeliveryAuto(true);
  };

  const submit = () => {
    setError('');
    if (sel.length === 0) { setError('Dodaj co najmniej jeden produkt ze sklepu.'); return; }
    if (sel.some((x) => x.qty < 1 || x.unit < 0)) { setError('Ilości muszą być dodatnie, a ceny nieujemne.'); return; }
    if (!(addr.firstName || addr.lastName || addr.companyName)) { setError('Podaj imię i nazwisko lub nazwę firmy odbiorcy.'); return; }
    if (deliveryMode === 'pickup' && !pickupPoint.trim()) { setError('Podaj kod punktu odbioru (np. OLS03H).'); return; }
    if (deliveryMode === 'address' && !(addr.street && addr.city && addr.zip)) { setError('Podaj pełny adres dostawy (ulica, miasto, kod pocztowy).'); return; }
    const digits = nip.replace(/[^0-9]/g, '');
    if (invRequired && digits.length !== 10) { setError('Faktura wymaga NIP (10 cyfr).'); return; }
    setSubmitting(true);
    const payload: any = {
      items: sel.map((it) => ({
        product_id: it.pid.split(':')[0], combination_id: it.combination_id, name: it.name, quantity: it.qty, unit_price: it.unit,
        base_price: (it.base != null ? it.base : it.unit), reference: it.reference,
        ean: it.ean, image_url: it.image_url, vat_rate: null,
      })),
      first_name: addr.firstName, last_name: addr.lastName, company_name: (addr.companyName || null),
      email: (addr.email || null), phone: (addr.phone || null),
      street: addr.street, city: addr.city, zip: addr.zip, country: addr.country,
      carrier_id: (carrierId || null), carrier_name: carrierName, delivery_cost: dcost,
      delivery_mode: deliveryMode, pickup_point: (deliveryMode === 'pickup' ? (pickupPoint.trim() || null) : null),
      inv_required: invRequired, inv_nip: (invRequired ? digits : null),
      inv_company: (invRequired ? (inv.company || null) : null),
      inv_street: (invRequired && !invSame ? (inv.street || null) : null),
      inv_city: (invRequired && !invSame ? (inv.city || null) : null),
      inv_zip: (invRequired && !invSame ? (inv.zip || null) : null),
      payment_type: paymentType, is_paid: isPaid, buyer_message: (message || null),
    };
    axios.post(API + '/orders/manual', payload, { headers: auth() })
      .then((r: any) => { setCreated(r.data); onCreated(); })
      .catch((e: any) => setError((e.response && e.response.data && e.response.data.detail) || 'Nie udało się utworzyć zamówienia.'))
      .finally(() => setSubmitting(false));
  };

  const hideImg = (e: any) => { if (e && e.currentTarget) e.currentTarget.style.display = 'none'; };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 860, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box' as any }}
        onClick={(e: any) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>🛒 Nowe zamówienie (sklep PrestaShop)</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
        </div>

        {created ? (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, padding: 18, fontSize: 14, marginBottom: 12 }}>
              <b>Utworzono zamówienie {created.order_number}</b><br />
              Suma: <b>{Number(created.total).toFixed(2)} {created.currency || 'PLN'}</b>
            </div>
            <button onClick={onClose} style={s.btnPrimary}>Zamknij</button>
          </div>
        ) : (
          <div>
            {error ? (<div style={{ background: '#fef2f2', border: '1px solid #fecdd3', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{error}</div>) : null}

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>1. Produkty ze sklepu</div>
              {notConfigured ? (
                <div style={{ fontSize: 13, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10 }}>
                  Sklep PrestaShop nie jest połączony. Przejdź do <b>Ustawienia → PrestaShop</b> i wpisz adres sklepu oraz klucz API.
                </div>
              ) : (
                <div>
                  <input value={pq} onChange={(e: any) => setPq(e.target.value)} placeholder="Szukaj: nazwa, SKU lub EAN…" style={{ ...s.input, marginBottom: 10 }} />
                  {(pwarn || []).length > 0 ? (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 10 }}>
                      {(pwarn || []).map((w: string, i: number) => (<div key={i}>{String(w)}</div>))}
                    </div>
                  ) : null}
                  {ploading ? <div style={{ fontSize: 13, color: '#6b7280', padding: 8 }}>Ładowanie produktów…</div> : null}
                  {perror ? <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 8 }}>{perror}</div> : null}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                    {products.map((p: any) => {
                      const pidS = String(p.id);
                      const combos: any[] = Array.isArray(p.combinations) ? p.combinations : [];
                      const hasVar = combos.length > 0;
                      const qn = pq.trim().toLowerCase();
                      const isHit = (c: any) => !!qn && (String(c.reference || '').toLowerCase().indexOf(qn) >= 0 || String(c.ean13 || '').indexOf(qn) >= 0 || String(c.label || '').toLowerCase().indexOf(qn) >= 0);
                      const sorted = combos.slice().sort((x: any, y: any) => (isHit(y) ? 1 : 0) - (isHit(x) ? 1 : 0));
                      const open = !!varOpen[pidS];
                      const shown = open ? sorted : sorted.slice(0, 6);
                      const inSel = !hasVar && sel.find((x) => x.pid === pidS);
                      return (
                        <div key={pidS} onClick={() => { if (!hasVar) addProduct(p); }}
                          style={{ border: '1px solid ' + (inSel ? '#86efac' : '#e5e7eb'), background: inSel ? '#f0fdf4' : '#fff', borderRadius: 8, padding: 10, cursor: hasVar ? 'default' : 'pointer' }}>
                          <div style={{ display: 'flex', gap: 10 }}>
                            {p.image_url ? <img src={p.image_url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} onError={hideImg} /> : <div style={{ width: 56, height: 56, background: '#f3f4f6', borderRadius: 6, flexShrink: 0 }} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2, maxHeight: 34, overflow: 'hidden' }}>{p.name}</div>
                              <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {p.reference ? <span>Ref: {p.reference} </span> : null}
                                {p.ean13 ? <span>EAN: {p.ean13}</span> : null}
                              </div>
                              {hasVar ? (
                                <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>Wybierz wariant ({combos.length}):</div>
                              ) : (
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>
                                  {Number(p.price_brutto).toFixed(2)} PLN
                                  {p.price_brutto_base != null && Number(p.price_brutto_base) > Number(p.price_brutto) ? (
                                    <span style={{ color: '#9ca3af', textDecoration: 'line-through', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>{Number(p.price_brutto_base).toFixed(2)}</span>
                                  ) : null}
                                </div>
                              )}
                            </div>
                            {!hasVar ? <div style={{ alignSelf: 'center', fontSize: 18, color: inSel ? '#15803d' : '#9ca3af' }}>{inSel ? '✓' : '+'}</div> : null}
                          </div>
                          {hasVar ? (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {shown.map((c: any) => {
                                const vSel = sel.find((x) => x.pid === pidS + ':' + String(c.id));
                                const hit = isHit(c);
                                return (
                                  <div key={String(c.id)} onClick={(e: any) => { e.stopPropagation(); addProduct(p, c); }}
                                    style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', border: '1px solid ' + (vSel ? '#86efac' : (hit ? '#c4b5fd' : '#eef0f3')), background: vSel ? '#f0fdf4' : (hit ? '#f5f3ff' : '#fafafa') }}>
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      <b>{String(c.label || c.reference || ('wariant ' + String(c.id)))}</b>
                                      {c.reference && c.label ? <span style={{ color: '#6b7280' }}> · {String(c.reference)}</span> : null}
                                    </span>
                                    <span style={{ fontWeight: 600, color: '#1d4ed8', flexShrink: 0 }}>{Number(c.price_brutto).toFixed(2)}</span>
                                    <span style={{ color: vSel ? '#15803d' : '#9ca3af', flexShrink: 0 }}>{vSel ? '✓' : '+'}</span>
                                  </div>
                                );
                              })}
                              {sorted.length > 6 ? (
                                <button onClick={(e: any) => { e.stopPropagation(); setVarOpen((prev) => { const n: any = { ...prev }; n[pidS] = !open; return n; }); }}
                                  style={{ ...s.btn, padding: '2px 8px', fontSize: 11, alignSelf: 'flex-start' }}>{open ? 'Mniej wariantów' : ('Wszystkie warianty (' + sorted.length + ')')}</button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  {(hasMore || products.length > 12) ? (
                    <div style={{ textAlign: 'center', marginTop: 10, display: 'flex', gap: 8, justifyContent: 'center' }}>
                      {hasMore ? <button onClick={() => searchProducts(pq, ppage + 1, true)} style={s.btn}>Pokaż więcej produktów</button> : null}
                      {products.length > 12 ? <button onClick={showLess} style={s.btn}>Pokaż mniej</button> : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>2. Wybrane produkty i ceny</div>
              {sel.length === 0 ? (
                <div style={{ fontSize: 13, color: '#6b7280' }}>Brak produktów — kliknij produkt powyżej, aby go dodać.</div>
              ) : (
                <div>
                  {sel.map((it) => (
                    <div key={it.pid} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
                      {it.image_url ? <img src={it.image_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} onError={hideImg} /> : <div style={{ width: 40, height: 40, background: '#f3f4f6', borderRadius: 6, flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{it.name}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          {it.reference ? <span>Ref: {it.reference} </span> : null}{it.ean ? <span>EAN: {it.ean}</span> : null}
                          {it.base != null && it.base > it.unit ? <span> · katalogowa: {it.base.toFixed(2)}</span> : null}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Ilość</div>
                        <input type="number" min={1} value={it.qty} onChange={(e: any) => setQty(it.pid, e.target.value)} style={{ ...s.input, width: 64 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Cena brutto/szt.</div>
                        <input value={String(it.unit)} onChange={(e: any) => setUnit(it.pid, e.target.value)} style={{ ...s.input, width: 90 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>× mnożnik</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input value={itemMult[it.pid] || ''} placeholder="0,7" onChange={(e: any) => setItemMultValue(it.pid, e.target.value)} style={{ ...s.input, width: 64 }} />
                          <button onClick={() => applyMultItem(it.pid)} style={{ ...s.btn, padding: '6px 8px' }}>↺</button>
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1d4ed8', width: 90, textAlign: 'right' }}>{(it.unit * it.qty).toFixed(2)}</div>
                      <button onClick={() => setSel((prev) => prev.filter((x) => x.pid !== it.pid))} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Mnożnik dla wszystkich cen (np. 0,7 = rabat 30%):</span>
                    <input value={mult} placeholder="0,7" onChange={(e: any) => setMult(e.target.value)} style={{ ...s.input, width: 80 }} />
                    <button onClick={applyMultAll} style={s.btn}>Zastosuj ×</button>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13, marginTop: 8, color: '#374151' }}>Produkty razem: <b>{itemsTotal.toFixed(2)} PLN</b></div>
                </div>
              )}
            </div>

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>3. Dostawa</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div>
                  <label style={s.label}>Metoda wysyłki (ze sklepu)</label>
                  <select value={carrierId} onChange={(e: any) => onCarrierChange(e.target.value)} style={s.input}>
                    <option value="">— wybierz —</option>
                    {carriers.map((c: any) => <option key={String(c.id)} value={String(c.id)}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Koszt dostawy (PLN)</label>
                  <input value={deliveryCost} placeholder="0,00" onChange={(e: any) => { setDeliveryCost(e.target.value); setDeliveryAuto(false); }} style={s.input} />
                  {carrierId && quote ? (
                    <div style={{ fontSize: 11, color: quote.available === false ? '#b91c1c' : '#6b7280', marginTop: 4 }}>
                      {String(quote.info || '')}
                      {!deliveryAuto && quote.cost != null ? (
                        <span> · <a href="#" onClick={(e: any) => { e.preventDefault(); setDeliveryAuto(true); setDeliveryCost(Number(quote.cost).toFixed(2)); }}>użyj ceny z cennika ({Number(quote.cost).toFixed(2)} zł)</a></span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              {isPickupCarrier ? (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                  <label style={s.label}>Punkt odbioru — kod (np. OLS03H, BGZ01G) *</label>
                  <input value={pickupPoint} placeholder="np. OLS03H" onChange={(e: any) => setPickupPoint(e.target.value)} style={{ ...s.input, maxWidth: 320 }} />
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Dostawa do punktu — adres dostawy nie jest wymagany dla tej metody.</div>
                </div>
              ) : null}
              {isCodCarrier ? (
                <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>Metoda "za pobraniem" — płatność ustawiona na "Za pobraniem (COD)".</div>
              ) : null}
            </div>

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>4. Odbiorca i adres dostawy{deliveryMode === 'pickup' ? <span style={{ fontWeight: 400, fontSize: 12, color: '#6b7280' }}> (opcjonalny przy dostawie do punktu)</span> : null}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                <div><label style={s.label}>Imię</label><input value={addr.firstName} onChange={setAddrField('firstName')} style={s.input} /></div>
                <div><label style={s.label}>Nazwisko</label><input value={addr.lastName} onChange={setAddrField('lastName')} style={s.input} /></div>
                <div><label style={s.label}>Firma (opcjonalnie)</label><input value={addr.companyName} onChange={setAddrField('companyName')} style={s.input} /></div>
                <div><label style={s.label}>Telefon</label><input value={addr.phone} onChange={setAddrField('phone')} style={s.input} /></div>
                <div><label style={s.label}>E-mail</label><input value={addr.email} onChange={setAddrField('email')} style={s.input} /></div>
                <div><label style={s.label}>Kraj</label>
                  <select value={addr.country} onChange={(e: any) => setAddr((prev) => ({ ...prev, country: e.target.value }))} style={s.input}>
                    {Object.keys(COUNTRIES).map((k) => <option key={k} value={k}>{COUNTRIES[k]}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: 'span 2' }}><label style={s.label}>Ulica i numer</label><input value={addr.street} onChange={setAddrField('street')} style={s.input} /></div>
                <div><label style={s.label}>Kod pocztowy</label><input value={addr.zip} onChange={setAddrField('zip')} placeholder="00-000" style={s.input} /></div>
                <div style={{ gridColumn: 'span 2' }}><label style={s.label}>Miasto</label><input value={addr.city} onChange={setAddrField('city')} style={s.input} /></div>
              </div>
            </div>

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>5. Faktura</div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={invRequired} onChange={(e: any) => setInvRequired(e.target.checked)} /> Zamówienie z fakturą (wymaga NIP)
              </label>
              {invRequired ? (
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <label style={s.label}>NIP</label>
                      <input value={nip} onChange={(e: any) => setNip(e.target.value)} placeholder="1234567890" style={s.input} />
                    </div>
                    <button onClick={fetchNip} disabled={nipLoading} style={{ ...s.btn, opacity: nipLoading ? 0.5 : 1 }}>{nipLoading ? '⏳ Pobieranie…' : 'Pobierz dane z Białej Listy'}</button>
                  </div>
                  {nipInfo ? (
                    <div style={{ background: nipInfo.found ? '#f0fdf4' : '#fef2f2', border: '1px solid ' + (nipInfo.found ? '#bbf7d0' : '#fecdd3'), borderRadius: 8, padding: 10, fontSize: 12, marginBottom: 8, color: '#374151' }}>
                      {nipInfo.found ? (
                        <div>
                          <b>{nipInfo.name}</b>{nipInfo.status_vat ? <span> · status VAT: {nipInfo.status_vat}</span> : null}
                          {nipInfo.account_numbers && nipInfo.account_numbers.length ? <div style={{ marginTop: 4, color: '#6b7280' }}>Konta: {nipInfo.account_numbers.join(' · ')}</div> : null}
                        </div>
                      ) : <span>{nipInfo.reason || 'Nie znaleziono podmiotu o tym NIP.'}</span>}
                    </div>
                  ) : null}
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={invSame} onChange={(e: any) => setInvSame(e.target.checked)} /> Adres faktury taki sam jak dostawa
                  </label>
                  {!invSame ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                      <div style={{ gridColumn: 'span 2' }}><label style={s.label}>Nazwa na fakturze</label><input value={inv.company} onChange={(e: any) => setInv((prev) => ({ ...prev, company: e.target.value }))} style={s.input} /></div>
                      <div style={{ gridColumn: 'span 2' }}><label style={s.label}>Ulica (faktura)</label><input value={inv.street} onChange={(e: any) => setInv((prev) => ({ ...prev, street: e.target.value }))} style={s.input} /></div>
                      <div><label style={s.label}>Kod pocztowy</label><input value={inv.zip} onChange={(e: any) => setInv((prev) => ({ ...prev, zip: e.target.value }))} style={s.input} /></div>
                      <div><label style={s.label}>Miasto</label><input value={inv.city} onChange={(e: any) => setInv((prev) => ({ ...prev, city: e.target.value }))} style={s.input} /></div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>6. Płatność</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={s.label}>Sposób płatności</label>
                  <select value={paymentType} onChange={(e: any) => { setPaymentType(e.target.value); setPayAuto(false); }} style={s.input}>
                    {PAYMENTS.map((pair) => <option key={pair[0]} value={pair[0]}>{pair[1]}</option>)}
                  </select>
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer', alignSelf: 'end', paddingBottom: 8 }}>
                  <input type="checkbox" checked={isPaid} onChange={(e: any) => setIsPaid(e.target.checked)} /> Zamówienie opłacone
                </label>
              </div>
            </div>

            <div style={s.card}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>7. Uwagi kupującego (opcjonalnie)</div>
              <textarea value={message} onChange={(e: any) => setMessage(e.target.value)} style={{ ...s.input, minHeight: 60, resize: 'vertical' as any }} placeholder="np. prośba o szybką wysyłkę…" />
            </div>

            <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: '#374151' }}>
                Produkty: {itemsTotal.toFixed(2)} PLN · Dostawa: {dcost.toFixed(2)} PLN · <b>Razem: {total.toFixed(2)} PLN</b>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={s.btn}>Anuluj</button>
                <button onClick={submit} disabled={submitting || sel.length === 0} style={{ ...s.btnPrimary, opacity: (submitting || sel.length === 0) ? 0.5 : 1, cursor: (submitting || sel.length === 0) ? 'not-allowed' : 'pointer' }}>
                  {submitting ? '⏳ Tworzenie…' : 'Utwórz zamówienie'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
